







































import { Component, Vue } from 'vue-property-decorator';
import { authModule } from '@/store';
import {
  deadlinePhrase,
  noticeSeverity,
  outstandingObligations,
  NoticeSeverity,
  Obligations,
} from '@/accountNotices';

const DISMISSED_KEY = 'account-notices-dismissed';

/**
 * One banner for everything a logged-in user still owes.
 *
 * Replaces `VerificationAlert` (email) and `TermsAlert` (terms), which ran side
 * by side so the user could judge from seeing them whether they should merge.
 * They should: two stacked banners above the router view is worse than one that
 * can say two things, and the whole bidder base sees both at once - see the
 * scale note below.
 *
 * It carries no actions of its own beyond the link. Acceptance in particular
 * belongs under the terms text, where the user has been shown what they are
 * agreeing to, and a resend button wants room for its cooldown and its 429. All
 * of that lives on `views/AccountNotices.vue`; this only has to be noticed.
 *
 * Scale note: `termsAcceptanceRequired` is true for anyone with no acceptance
 * on file at all, and acceptances have only been recorded since the
 * registration checkbox shipped - so the entire existing bidder base sees this
 * the day it ships. Hence the tone: a routine confirmation, not a warning that
 * something is wrong with the account.
 */
@Component
export default class AccountNoticesAlert extends Vue {
  /**
   * What was outstanding when the user last dismissed this, as a signature.
   *
   * Not a bare boolean: dismissing "accept the terms" must not also swallow
   * "confirm your email" if that turns up later in the same tab - which it can,
   * since a material terms bump adds an obligation to an account that had none.
   * Storing what was dismissed means a *different* set brings the banner back.
   */
  dismissedSignature: string = '';

  created() {
    // sessionStorage, so the banner stays out of the way for this tab but comes
    // back on the next visit until the user actually does something about it.
    this.dismissedSignature =
      window.sessionStorage.getItem(DISMISSED_KEY) || '';
  }

  get obligations(): Obligations {
    return outstandingObligations();
  }

  get signature(): string {
    const { verification, terms } = this.obligations;
    return `${verification ? 'v' : ''}${terms ? 't' : ''}`;
  }

  get both(): boolean {
    return this.obligations.verification && this.obligations.terms;
  }

  get email(): string {
    const user = authModule.user;
    return user ? user.email : '';
  }

  /**
   * The deadline, once it is close enough to name. '' the rest of the time.
   *
   * Nothing is said while the window is unset or comfortably far off: the
   * routine tone above is the point, and a date three months out would only
   * teach people to ignore the banner. Silent once closed too - the banner keeps
   * its housekeeping tone and the consequence is stated on the page.
   */
  get deadline(): string {
    return deadlinePhrase();
  }

  /**
   * The banner's colour, shared with the notices page via `noticeSeverity()`.
   *
   * Never decided here: the banner and the page must not disagree about how
   * urgent the same account's situation is.
   */
  get severity(): NoticeSeverity {
    return noticeSeverity();
  }

  /**
   * Dismissible only while this is routine - so, while `severity` is `info`.
   *
   * Deliberately the same test as the colour rather than a threshold of its own:
   * urgency is one idea, and a banner that has gone amber to say "this is
   * closing" should not also offer a button that makes it go away. Once the
   * deadline is inside `DEADLINE_NOTICE_DAYS` it stays put, and it is still
   * there after the window closes.
   *
   * Revised 2026-08-13. P4-09 originally dropped the dismiss control on
   * `closed` alone, as the persistent half of a mild-but-persistent pairing: the
   * banner held one tone throughout, and permanence was what compensated. That
   * pairing is gone now the banner escalates, so the user re-decided this - the
   * runway exists to collect acceptances *before* anything is refused, and a
   * dismissed banner collects none.
   *
   * The cost is accepted knowingly: `termsAcceptanceRequired` is true for the
   * whole pre-existing bidder base, so this is a week of unavoidable banner
   * above every page for effectively every bidder at once. That is the point,
   * and it is why the copy stays a polite request rather than a warning.
   */
  get dismissible(): boolean {
    return this.severity === 'info';
  }

  get visible(): boolean {
    if (!this.obligations.any) {
      return false;
    }
    // The stored dismissal is ignored, not cleared, whenever the banner is not
    // dismissible: if the deadline is ever pushed back out (the documented
    // rollback path) the user's earlier dismissal is still theirs and takes
    // effect again - which now also covers a date moved from inside the 7-day
    // window to outside it, not just a reopened window.
    if (this.dismissible && this.signature === this.dismissedSignature) {
      return false;
    }
    // Not shown on the notices page itself - the user is already there, being
    // told the same thing at length.
    return this.$route.name !== 'account-notices';
  }

  dismiss() {
    // Belt and braces: with `dismissible` false there is no control to click,
    // but nothing else should be able to record a dismissal either.
    if (!this.dismissible) {
      return;
    }
    this.dismissedSignature = this.signature;
    window.sessionStorage.setItem(DISMISSED_KEY, this.signature);
  }
}
